import type { QsoType, LogbookType, PaginationResult } from "@/types";

import QsoPouchRepo from "@/repos/QsoPouchRepo";
import type { QsoServiceInterface } from "@/interfaces";
import LogbookService from "./LogbookService";

// Safely extract a logbook ID from a QSO ID
// Expected QSO ID format: QSO:LOGBOOK:<logbookId>:...
// Returns: LOGBOOK:<logbookId> or null if malformed
const extractLogbookIdFromQsoId = (qsoId: string | null | undefined): string | null => {
  if (typeof qsoId !== "string") return null;
  const parts = qsoId.split(":");
  if (parts.length >= 3 && parts[0] === "QSO") {
    return `LOGBOOK:${parts[2]}`;
  }
  return null;
};

const QsoService: QsoServiceInterface = {
  async create(params: { attrs: QsoType, logbook: LogbookType }): Promise<QsoType> {
    const qso = await QsoPouchRepo.create(params);
    await LogbookService.update(params.logbook);
    return qso;
  },
  async batchCreate(params: { attrs: QsoType, logbook: LogbookType }): Promise<QsoType[]> {
    const createdQsos = await createQsosForMultipleEntries(params);
    await LogbookService.update(params.logbook);
    return createdQsos;
  },
  async update(qso: QsoType, options?: { fromQrzUpload?: boolean }): Promise<QsoType> {
    return QsoPouchRepo.update(qso, options);
  },
  async findAllByLogbook(logbookId: string): Promise<QsoType[]> {
    return QsoPouchRepo.findAllByLogbook(logbookId);
  },
  async findAllByCallsign(callsign: string): Promise<QsoType[]> {
    return QsoPouchRepo.findAllByCallsign(callsign);
  },
  async countAllByLogbook(logbookId: string): Promise<number> {
    return QsoPouchRepo.countAllByLogbook(logbookId);
  },
  async deleteAllByLogbook(logbookId: string): Promise<QsoType[]> {
    return QsoPouchRepo.deleteAllByLogbook(logbookId);
  },
  async findAll(): Promise<QsoType[]> {
    return QsoPouchRepo.findAll();
  },
  async findOne(id: string): Promise<QsoType> {
    return QsoPouchRepo.findOne(id);
  },
  async delete(id: string): Promise<QsoType | null> {
    const deleted = await QsoPouchRepo.delete(id);
    const logbookId = extractLogbookIdFromQsoId(id);
    if (logbookId) {
      try {
        const logbook = await LogbookService.findOne(logbookId);
        await LogbookService.update(logbook);
      } catch (err) {
        console.error('Failed to update logbook count after QSO delete:', err);
      }
    }
    return deleted;
  },
  async fetchAllWithPaging(page: number, limit: number, searchQuery: string): Promise<PaginationResult<QsoType>> {
    return QsoPouchRepo.fetchAllWithPaging(page, limit, searchQuery);
  },
  async search(searchQuery: string): Promise<QsoType[]> {
    return QsoPouchRepo.search(searchQuery);
  },
  async searchOperators(callsign: string, limit?: number): Promise<QsoType[]> {
    return QsoPouchRepo.searchOperators(callsign, limit);
  }
};

export const createQsosForMultiplePotaRefs = async (params: { attrs: QsoType, logbook: LogbookType }): Promise<QsoType[]> => {
  const { attrs, logbook } = params;

  // Split the potaRef by comma, trim each value, and filter out empty strings
  const parks = attrs.potaRef!.split(',')
    .map(park => park.trim())
    .filter(park => park !== "");  // Remove empty values

  const createdQsos: QsoType[] = [];

  for (const park of parks) {
    // Create a new QSO for each valid park, modifying only the potaRef
    const qsoForPark = { ...attrs, potaRef: park };
    const createdQso = await QsoPouchRepo.create({ attrs: qsoForPark, logbook });
    createdQsos.push(createdQso);
  }

  return createdQsos;
};

export const createQsosForMultipleEntries = async (params: { attrs: QsoType, logbook: LogbookType }): Promise<QsoType[]> => {
  const { attrs, logbook } = params;

  // Extract and clean up callsigns
  const callsigns = attrs.call?.split(',')
    .map(sign => sign.trim())
    .filter(sign => sign) || [];

  // Extract and clean up parks if provided
  const parks = attrs.potaRef
    ? attrs.potaRef.split(',').map(ref => ref.trim()).filter(ref => ref)
    : [];

  const createdQsos: QsoType[] = [];

  if (parks.length > 0) {
    // Case 1: `potaRef` is provided, create a QSO for each combination of callsign and park
    for (const callsign of callsigns) {
      for (const park of parks) {
        const qsoForCombination = { ...attrs, potaRef: park, call: callsign };
        const createdQso = await QsoPouchRepo.create({ attrs: qsoForCombination, logbook });
        createdQsos.push(createdQso);
      }
    }
  } else {
    // Case 2: `potaRef` is not provided, create a QSO for each callsign only
    for (const callsign of callsigns) {
      const qsoForCallsign = { ...attrs, call: callsign };
      const createdQso = await QsoPouchRepo.create({ attrs: qsoForCallsign, logbook });
      createdQsos.push(createdQso);
    }
  }

  return createdQsos;
};

const QsoServiceWithMove = {
  ...QsoService,
  async moveQsosToLogbook(qsoIds: string[], targetLogbookId: string): Promise<{ success: string[], failed: string[] }> {
    if (!qsoIds.length) {
      return { success: [], failed: [] };
    }

    if (!targetLogbookId?.trim()) {
      console.warn('moveQsosToLogbook: targetLogbookId is required');
      return { success: [], failed: [...qsoIds] };
    }

    // Get source logbook ID from first QSO ID (format: QSO:LOGBOOK:xxx:yyy)
    const sourceLogbookId = extractLogbookIdFromQsoId(qsoIds[0]);

    // Optional: if UI somehow selected the same source/target, treat as no-op
    if (sourceLogbookId && sourceLogbookId === targetLogbookId) {
      return { success: [...qsoIds], failed: [] };
    }

    // Perform the move operation
    const results = await QsoPouchRepo.moveQsosToLogbook(qsoIds, targetLogbookId);

    // Update logbook counts if we have a source logbook
    if (sourceLogbookId && results.success.length > 0) {
      try {
        const doTargetUpdate = sourceLogbookId !== targetLogbookId;
        const sourceLogbook = await LogbookService.findOne(sourceLogbookId);
        const targetLogbook = doTargetUpdate
          ? await LogbookService.findOne(targetLogbookId)
          : null;
        await Promise.all([
          LogbookService.update(sourceLogbook),
          doTargetUpdate && targetLogbook
            ? LogbookService.update(targetLogbook)
            : Promise.resolve()
        ]);
      } catch (error) {
        console.error('Failed to update logbook counts:', error);
      }
    }

    return results;
  },

  /**
   * Bulk update QSOs with partial data
   * @param qsoIds Array of QSO IDs to update
   * @param updates Partial QSO data to apply to all QSOs
   * @returns Object with arrays of successful and failed QSO IDs
   */
  async bulkUpdateQsos(qsoIds: string[], updates: Partial<QsoType>): Promise<{ success: string[], failed: string[] }> {
    const results = { success: [] as string[], failed: [] as string[] };
    
    for (const qsoId of qsoIds) {
      try {
        // Fetch the existing QSO
        const existingQso = await QsoPouchRepo.findOne(qsoId);
        if (!existingQso) {
          results.failed.push(qsoId);
          continue;
        }
        
        // Merge updates with existing QSO data
        const updatedQso: QsoType = {
          ...existingQso,
          ...updates,
          // Preserve critical fields that should never be overwritten
          _id: existingQso._id,
          _rev: existingQso._rev,
          createdAt: existingQso.createdAt,
          updatedAt: new Date().toISOString(),
        };
        
        // Update the QSO
        await QsoPouchRepo.update(updatedQso);
        results.success.push(qsoId);
      } catch (error) {
        console.error(`Failed to update QSO ${qsoId}:`, error);
        results.failed.push(qsoId);
      }
    }
    
    return results;
  }
};

export default QsoServiceWithMove;
